import type { FC } from 'react';

import { Placeholder, useCms, useRendering, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { ButtonLink, Heading, PageGrid, Stack, Text, TextLink } from '@eneco/sparky';
import type { HeadingProps, TextProps } from '@eneco/sparky/types';

import { ChatLink, type TextLinkColor } from './ChatLink';
import { resolveSlot, TITLE_TEXT_CTA_SLOTS } from './slots';
import type { TitleTextCTAContent } from './title-text-cta-content.types';

const headingSizes: Record<string, HeadingProps['size']> = {
  headingDefault: { initial: 'S', md: 'M' },
  headingHero: { initial: 'M', md: 'XL' },
  headingSub: { initial: 'XS', md: 'S' },
};

const LINK_COLORS = new Set<TextLinkColor>(['linkBrand', 'linkPrimary', 'linkInverted']);

export interface TitleTextCTAViewProps {
  /** Already resolved — `TitleTextCTA` passes `content.title` verbatim, `PersonalizedTitleTextCTA`
   * passes the interpolated/fallback title it computed. */
  title?: string;
  content: Omit<TitleTextCTAContent, 'title'>;
}

/**
 * Shared render body for `TitleTextCTA` and `PersonalizedTitleTextCTA` — identical below the title on
 * both providers, since the legacy Sitecore `PersonalizedTitleTextCTA`
 * (`libs/sitecore/components/content/src/PersonalizedTitleTextCTA/PersonalizedTitleTextCTA.tsx`) is
 * itself a thin wrapper that only ever overrides `TitleTextCTA`'s `title`, passing everything else
 * through unchanged. Extracted here so neither agnostic-lane block carries its own copy of this body.
 *
 * `buttonLink`/`link` carry their own display text, which the converted link keeps; a link without text
 * is skipped rather than rendered unlabelled.
 *
 * Drops the Sitecore Experience Editor `wrap()` calls — this lane never runs inside the Experience
 * Editor.
 */
export const TitleTextCTAView: FC<TitleTextCTAViewProps> = ({ title, content }) => {
  const { adapter } = useCms();
  const rendering = useRendering();

  const anchorId = useSetting('anchorId');
  const headingLevel = useSetting('headingLevel') ?? 'h2';
  const headingSize = useSetting('headingSize') ?? 'headingDefault';
  const textSize = useSetting('textSize') as TextProps['size'] | undefined;
  const linkColorSetting = useSetting('linkColor') as TextLinkColor | undefined;
  const linkColor = linkColorSetting && LINK_COLORS.has(linkColorSetting) ? linkColorSetting : undefined;

  const richText = adapter.toRichText(content.content);
  const mainSlot = resolveSlot(rendering, TITLE_TEXT_CTA_SLOTS.main);
  const button = linkWithUrl(content.buttonLink);
  const textLink = linkWithUrl(content.link);

  return (
    <PageGrid.Item gridColumn={{ initial: '1/-1', md: '1/-4' }}>
      <div id={anchorId}>
        <Stack gap="6">
          {title && (
            <Heading as={headingLevel as HeadingProps['as']} size={headingSizes[headingSize]} color="inherit">
              {title}
            </Heading>
          )}
          {richText && (
            <Text size={textSize ?? { initial: 'BodyM', md: 'BodyL' }}>
              <RichText html={richText} replacements={{ a: RichTextLink, strong: RichTextStrong }} />
            </Text>
          )}
          {mainSlot ? <Placeholder name={mainSlot} /> : null}
          {(button || textLink) && (
            <Stack.Item grow={false}>
              <Stack direction="row" gap="4" alignY="center">
                {button?.text && (
                  <ButtonLink href={button.href} target={button.target}>
                    {button.text}
                  </ButtonLink>
                )}
                {textLink?.text && (
                  <TextLink href={textLink.href} target={textLink.target} emphasis="high" color={linkColor}>
                    {textLink.text}
                  </TextLink>
                )}
              </Stack>
            </Stack.Item>
          )}
          {content.chatLink && (
            <Stack.Item grow={false}>
              <ChatLink chatLink={content.chatLink} linkColor={linkColor} />
            </Stack.Item>
          )}
        </Stack>
      </div>
    </PageGrid.Item>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RichTextLink: FC<any> = props => <TextLink {...props}>{props.children}</TextLink>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RichTextStrong: FC<any> = props => (
  <Text display="inline" weight="bold">
    {props.children}
  </Text>
);
